import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/blue-stock-trailer-tan-tarp.jpg";
import heroPicture from "@/assets/blue-stock-trailer-tan-tarp.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

export const Route = createFileRoute("/farming-ranching-series")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Agricultural Farming & Ranching Series — Ranch Covers & Fabrication | Texas Made Shade",
      description:
        "Farming & Ranching Series — stock trailer covers, hay tarps, livestock shade and welded ranch fabrication for San Antonio, Austin and Texas ranches.",
      image: hero,
      socialTitle: "Farming & Ranching Series — Built for Working Texas Land",
      socialDescription:
        "Stock trailer tarps, equipment covers, livestock shade and welded ranch fabrication from a Texas shop that understands working operations.",
      keywords:
        "farming and ranching covers texas, stock trailer covers, hay tarps, livestock shade structures, ranch welding fabrication, hog trap cages, farm equipment covers, seguin tx ranch covers, ranch covers san antonio, ranch tarps austin, livestock shade texas hill country, stock trailer tarps new braunfels, welded ranch gates texas statewide",
    }),
    scripts: serviceHeadScripts({
      path: "/farming-ranching-series",
      serviceName: "Farming & Ranching Series",
      breadcrumbName: "Farming & Ranching Series",
      description:
        "Agricultural covers, tarps, livestock shade and welded ranch fabrication for Texas farms, ranches and ag operations.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/farming-ranching-series"
      serviceName="Farming & Ranching Series"
      eyebrow="AGRICULTURAL — FARMING & RANCHING SERIES"
      title="Farming & Ranching Series — Ag Covers, Shade & Welded Fabrication"
      intro="Built for working land. The Farming & Ranching Series covers the fabric and metal your operation runs on — stock trailer tops, equipment and hay tarps, livestock shade, welded gates, panels and traps. Sewn and welded in Seguin, Texas."
      heroImage={heroPicture}
      preContent={
        <div className="mb-6 flex justify-center">
          <SeriesBadge slug="farming-ranching-series" size="md" priority />
        </div>
      }
      heroCta={{
        eyebrow: "Farming & Ranching Series · Statewide Texas",
        headline: "Covers Built for Working Operations.",
        subhead:
          "Send dimensions and a couple of photos — we'll quote your trailer top, tarp, shade or fabrication job, usually the same business day.",
        primaryHref: "/estimate-quotes#general",
        primaryLabel: "Request Ranch Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      bodyLayout="accordion"
      features={[
        "Stock and livestock trailer covers",
        "Hay, equipment and implement tarps",
        "Livestock and pen shade structures",
        "Welded gates, panels and ranch repairs",
        "Hog traps, cages and dog boxes",
        "Barn, shop and loafing shed covers",
      ]}
      applications={[
        "Cattle, goat and sheep operations",
        "Row crop and hay farms",
        "Feed lots, sale barns and show barns",
        "Ranch trucks, trailers and implements",
        "Hunting leases and working ranch camps",
      ]}
      keywordOutline={[
        { id: "overview", label: "What the Ag Series Covers" },
        { id: "why-us", label: "Why Ranchers Call Us" },
        { id: "details", label: "Materials & Fabrication" },
        { id: "applications", label: "Who We Build For" },
        { id: "faqs", label: "Farming & Ranching FAQs" },
      ]}
      body={[
        {
          heading: "What the Farming & Ranching Series Covers",
          text: "Fabric and metal for working operations — the stuff that has to survive sun, dust, stock and daily use.",
          bullets: [
            "Stock trailer tops, tarps and roll-up sides",
            "Hay, equipment and implement covers",
            "Livestock shade and pen covers",
            "Welded gates, panels, racks and frames",
            "Hog traps, cages, dog boxes and truck racks",
          ],
        },
        {
          heading: "Materials & Fabrication",
          text: "We use marine-grade fabric and heavy vinyl over welded aluminum and steel — the same hardware and thread we put on boat and commercial work.",
          bullets: [
            "Herculite® and marine-grade vinyl tarps",
            "Sunbrella® canvas where breathability matters",
            "In-house aluminum and steel welding",
            "Stainless hardware, heavy webbing and reinforced corners",
            "Repairs and recovers on existing tarps and frames",
          ],
        },
        {
          heading: "Why Ranchers Call Us",
          text: "Thirty-plus years of custom sewing and welding in Texas. We measure on-site when it matters, build to your equipment instead of a catalog size, and stand behind the repair.",
          bullets: [
            "Custom-built to your trailer, pen or implement",
            "One shop handles the sewing and the welding",
            "Statewide delivery and installation",
            "Trail Boss welding support for metal-only jobs",
          ],
        },
      ]}
    />
  ),
});
